import TourDescriptor from "@app/component/flows/types/TourDescriptor";

import { messages } from "./messages";
import {
  ensureSidebarClosedOnBefore,
  initializeAcaTourOnBefore,
  isOnlyCompaniesVisible,
  isOwnerFilterSetToCurrentUser,
  isPosStatusFilterSetToActive,
  prepareMapOnBefore,
  waitForDrawerOnBefore,
} from "./utils";

// Main navbar "Map" item. MenuItemTitle emits data-navbar-item={path}, and the Map item's path is
// Path.MAP ("/map"), so this attribute is a stable hook that survives styling changes.
const NAV_MAP_ITEM = '[data-navbar-item="/map"]';

// Entity visibility toggle (Companies/People/Deals) in the map filter bar. EntityVisibility renders
// its root with the CSS-module class EntityVisibility_container.
const ENTITY_VISIBILITY = "div[class*=MapPage_container] div[class*=EntityVisibility_container]";

// The map filter button carries a literal id.
const MAP_FILTER_BUTTON = "#map-filter-button";

// "Select filter type" primary button in the filter popover's empty state (NoFiltersState) — it is
// the only primary button there because step 2 cleared all filters. (Reused from JasperBulkEdit.)
const SELECT_FILTER_TYPE_BUTTON = ".mmc-data_map-filter-popover .ant-btn-primary";

// antd Dropdown item keyed by EntityType — COMPANY = "accounts"; the $= suffix match is unambiguous
// against the other keys (universal/contacts/deals). (Reused from JasperBulkEdit.)
const COMPANY_TYPE_MENU_ITEM = '.ant-dropdown-menu-item[data-menu-id$="accounts"]';

// The field-name selectbox of the just-added (not yet committed) rule. By step 9 two field
// selectboxes exist and the tour anchors by first match, which would land on the Owner row. A
// committed row's FieldColumn carries SimpleConditionRow_fieldName; the footer's new-field picker
// (ConditionsFooter renders FieldColumn with no extra className) does not — so :not() isolates it.
const NEW_RULE_FIELD_SELECTBOX =
  "[data-entity-type=accounts] [class*=FieldColumn_container]:not([class*=SimpleConditionRow_fieldName]) [class*=FieldColumn_fieldNameSelectBox]";

// The field-name selectbox inside the company block (FieldColumn renders it with the CSS-module
// class fieldNameSelectBox). (Reused from JasperBulkEdit.)
const COMPANY_FIELD_SELECTBOX =
  "[data-entity-type=accounts] [class*=FieldColumn_fieldNameSelectBox]";

// Owner value editor: UsersFilter -> UserMultiSelectField, whose root class is container. This is
// distinct from the option-field editor (OptionFilter_container), so it uniquely targets the Owner
// row's value box even after a second rule is added.
const OWNER_VALUE_BOX = "[data-entity-type=accounts] [class*=UserMultiSelectField_container]";

// "Add filter rule" text button — the first (and, at this point, only relevant) button in the
// conditions footer's Actions row.
const ADD_FILTER_RULE_BUTTON = "[data-entity-type=accounts] [class*=Actions_container] button";

// posstatus value editor: single-option custom field -> OptionFilter, root class container. Unique
// vs the Owner editor, so it targets the second rule's value box without index gymnastics.
const POSSTATUS_VALUE_BOX = "[data-entity-type=accounts] [class*=OptionFilter_container]";

// Zoom in/out buttons — their own wrapper inside the map controls (ToolSelectionPanel zoomControls),
// tighter than the whole mapControls group.
const ZOOM_CONTROLS = "div[class*=MapPage_container] div[class*=ToolSelectionPanel_zoomControls]";

// "Open List" is the 2nd command in TopCommandsBar (after Lead Finder); its button opens the
// sidebar. (Selectors reused from MapOnboarding's introduction tour.)
const OPEN_LIST =
  "div[class*=MapPage_container] div[class*=TopCommandsBar_container] > div:nth-child(2)";
const OPEN_LIST_BUTTON = `${OPEN_LIST} > button`;

// The record list sidebar (antd Drawer wrapped by BaseSidebar -> Sidebar_container).
const SIDEBAR = "div[class*=Sidebar_container] .ant-drawer-content";

// Internal analytics events (verb + AnalyticsContext keyPath). Filter events live under
// "Map -> Filters"; company field/type events under "Map -> Filters -> Company Filters".
const EVENT_FILTER_OPENED = "Clicked: Map -> Filters -> Filters";
const EVENT_COMPANY_TYPE = "Process: Map -> Filters -> Select Filter type -> Company";
const EVENT_SELECT_FIELD = "Using: Map -> Filters -> Company Filters -> Select Field";

export const acaActiveDealersTourConfiguration: TourDescriptor = {
  id: "mmc-aca-active-dealers",
  steps: [
    {
      id: "open-map",
      description: messages.step01Description,
      onBefore: initializeAcaTourOnBefore,
      options: {
        // the click navigates to /map; without this the tour hides itself on the route PUSH
        disableInteraction: false,
        expectRouteChange: true,
        showButtons: false,
      },
      selector: NAV_MAP_ITEM,
      title: messages.step01,
      waitForEvent: { eventType: "click", selector: NAV_MAP_ITEM },
    },
    {
      id: "show-companies-only",
      canGoNext: isOnlyCompaniesVisible,
      description: messages.step02Description,
      onBefore: prepareMapOnBefore,
      options: { disableInteraction: false },
      position: "bottom",
      selector: ENTITY_VISIBILITY,
      title: messages.step02,
    },
    {
      id: "open-filter",
      description: messages.step03Description,
      options: { disableInteraction: false, showButtons: false },
      position: "bottom",
      selector: MAP_FILTER_BUTTON,
      title: messages.step03,
      waitForEvent: {
        eventType: "internal",
        properties: { visible: true },
        selector: EVENT_FILTER_OPENED,
      },
    },
    {
      id: "select-filter-type",
      description: messages.step04Description,
      options: { disableInteraction: false, showBackground: false, showButtons: false },
      position: "right",
      selector: SELECT_FILTER_TYPE_BUTTON,
      title: messages.step04,
      waitForEvent: { eventType: "click", selector: SELECT_FILTER_TYPE_BUTTON },
    },
    {
      id: "choose-company",
      description: messages.step05Description,
      options: { disableInteraction: false, showBackground: false, showButtons: false },
      position: "right",
      selector: COMPANY_TYPE_MENU_ITEM,
      title: messages.step05,
      waitForEvent: { eventType: "internal", selector: EVENT_COMPANY_TYPE },
    },
    {
      id: "select-owner-field",
      description: messages.step06Description,
      options: { disableInteraction: false, showBackground: false, showButtons: false },
      position: "right",
      selector: COMPANY_FIELD_SELECTBOX,
      title: messages.step06,
      waitForEvent: {
        eventType: "internal",
        properties: { field: "Owner" },
        selector: EVENT_SELECT_FIELD,
      },
    },
    {
      id: "select-owner-value",
      canGoNext: isOwnerFilterSetToCurrentUser,
      description: messages.step07Description,
      // anchored inside the filter popover — preventMouseDown so pressing Next doesn't close it
      options: { disableInteraction: false, preventMouseDown: true, showBackground: false },
      position: "right",
      selector: OWNER_VALUE_BOX,
      title: messages.step07,
    },
    {
      id: "add-filter-rule",
      description: messages.step08Description,
      options: { disableInteraction: false, showBackground: false, showButtons: false },
      position: "right",
      selector: ADD_FILTER_RULE_BUTTON,
      title: messages.step08,
      waitForEvent: { eventType: "click", selector: ADD_FILTER_RULE_BUTTON },
    },
    {
      id: "select-posstatus-field",
      description: messages.step09Description,
      // any field pick in the company section advances; no field-name match (posstatus is
      // org-specific and the "Select Field" event only carries the display name)
      options: { disableInteraction: false, showBackground: false, showButtons: false },
      position: "right",
      selector: NEW_RULE_FIELD_SELECTBOX,
      title: messages.step09,
      waitForEvent: { eventType: "internal", selector: EVENT_SELECT_FIELD },
    },
    {
      id: "select-posstatus-value",
      canGoNext: isPosStatusFilterSetToActive,
      description: messages.step10Description,
      options: { disableInteraction: false, preventMouseDown: true, showBackground: false },
      position: "right",
      selector: POSSTATUS_VALUE_BOX,
      title: messages.step10,
    },
    {
      id: "close-filters",
      description: messages.step11Description,
      // centered, no anchor. preventMouseDown stays false so the Next click's mousedown reaches the
      // document and closes the filter popover (that is the point of this step).
      options: { disableInteraction: false, preventMouseDown: false },
      title: messages.step11,
    },
    {
      id: "zoom-controls",
      description: messages.step12Description,
      position: "left",
      selector: ZOOM_CONTROLS,
      title: messages.step12,
    },
    {
      id: "open-list",
      description: messages.step13Description,
      onBefore: ensureSidebarClosedOnBefore,
      options: { disableInteraction: false, showButtons: false },
      position: "left",
      selector: OPEN_LIST,
      title: messages.step13,
      waitForEvent: { eventType: "click", selector: OPEN_LIST_BUTTON },
    },
    {
      id: "records-list",
      description: messages.step14Description,
      onBefore: waitForDrawerOnBefore,
      position: "left",
      selector: SIDEBAR,
      title: messages.step14,
    },
  ],
};
